import styled from 'styled-components';
import { useId } from 'react';
import type { Ref, TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  // React 19 passes `ref` to function components as a normal prop (no forwardRef
  // needed) — declared explicitly, exactly like ui/TextField's, so callers that
  // need to focus the box imperatively are type-checked. LegalFloorReportPage is
  // the first: a validation bounce must move focus to the field that is wrong,
  // not leave it parked on the Send button.
  ref?: Ref<HTMLTextAreaElement>;
}

const Wrap = styled.div` display: flex; flex-direction: column; gap: 5px; `;
const Label = styled.label`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500; color: ${({ theme }) => theme.color.textMuted};
`;
const Area = styled.textarea`
  background: ${({ theme }) => theme.color.glassBg}; border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md}; padding: 10px 12px; min-height: 84px; resize: vertical;
  color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm}; outline: none;
  &:focus-visible { border-color: ${({ theme }) => theme.color.accentCyan};
    box-shadow: ${({ theme }) => theme.elevation.glow}; }
`;

export function Textarea({ label, ...rest }: TextareaProps) {
  const id = useId();
  return (
    <Wrap>
      <Label htmlFor={id}>{label}</Label>
      <Area id={id} {...rest} />
    </Wrap>
  );
}
